import { useState, useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import { ensureValidAccessToken } from '../lib/app-auth';
import { getSocket, subscribeSocketLifecycle } from '../lib/socket';

/**
 * Hook that provides access to the singleton Socket.IO client.
 * Returns the socket instance and connection status.
 *
 * Tracks singleton lifecycle: when auth events destroy + recreate the socket
 * (disconnectSocket/getSocket), the returned instance updates so consumer
 * effects that depend on `socket` re-attach their handlers to the live one.
 */
export function useSocket() {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  // Track the current singleton instance across auth-driven recreation.
  useEffect(() => {
    setSocket(getSocket());
    return subscribeSocketLifecycle(setSocket);
  }, []);

  // Per-instance connection tracking + lazy connect.
  useEffect(() => {
    if (!socket) {
      setConnected(false);
      return;
    }

    let isActive = true;

    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);

    // Set initial state
    setConnected(socket.connected);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Connect if not already connected
    if (!socket.connected) {
      void ensureValidAccessToken().then((token) => {
        if (!isActive || !token) {
          return;
        }

        socket.connect();
      });
    }

    return () => {
      isActive = false;
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      // Don't disconnect: socket is app-level singleton
    };
  }, [socket]);

  return { socket, connected };
}
